import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, IconButton, Tooltip, InputLabel, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Stepper, Step, StepLabel, StepContent, Radio, RadioGroup, FormControlLabel, Chip, Alert, Snackbar, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import medicineBazarBn from '../i18n/bn/medicineBazar';
import medicineBazarEn from '../i18n/en/medicineBazar';
import medicineBazarNp from '../i18n/np/medicineBazar';
import medicineBazarPk from '../i18n/pk/medicineBazar';
import medicineBazarBt from '../i18n/bt/medicineBazar';
import medicineBazarDe from '../i18n/de/medicineBazar';
import medicineBazarSl from '../i18n/sl/medicineBazar';
import medicineBazarAf from '../i18n/af/medicineBazar';
import medicineBazarCn from '../i18n/cn/medicineBazar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { useCart } from '../contexts/CartContext';
import { 
  LocalHospital, 
  Favorite, 
  Opacity, 
  Accessibility, 
  Spa, 
  ChildCare, 
  Face, 
  Bolt, 
  Restaurant, 
  FitnessCenter, 
  Elderly, 
  Science, 
  Psychology, 
  Coronavirus, 
  MedicalServices,
  Visibility,
  BugReport,
  Water as WaterDrop,
  Shield,
  LocalPharmacy,
  WbSunny,
  Vaccines,
  EmojiFoodBeverage,
  BrightnessLow,
  Store,
  Healing,
  Search,
  Description,
  LocationOn,
  Close,
  CheckCircle,
  Schedule,
  ContactPhone,
  Assignment,
  ArrowForward,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const PrescriptionUpload = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(45deg, #e3f2fd 30%, #bbdefb 90%)',
  border: '1px dashed #1e88e5',
}));

const MedicineCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for medicine stores
const DefaultPharmacyLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

// Mock data - Replace with actual API calls
const categories = [
  { name: 'অ্যান্টিবায়োটিক', englishName: 'Antibiotics', icon: <Science /> },
  { name: 'ব্যথা নিরাময়', englishName: 'Pain Relief', icon: <Healing /> },
  { name: 'হৃদরোগ সম্পর্কিত', englishName: 'Cardiovascular', icon: <Favorite /> },
  { name: 'ডায়াবেটিস', englishName: 'Diabetes', icon: <Opacity /> },
  { name: 'শ্বাসযন্ত্র', englishName: 'Respiratory', icon: <Coronavirus /> },
  { name: 'পরিপাকতন্ত্র', englishName: 'Gastrointestinal', icon: <Restaurant /> },
  { name: 'চর্মরোগ', englishName: 'Dermatology', icon: <Face /> },
  { name: 'মানসিক স্বাস্থ্য', englishName: 'Mental Health', icon: <Psychology /> },
  { name: 'ভিটামিন ও সম্পূরক', englishName: 'Vitamins & Supplements', icon: <FitnessCenter /> },
  { name: 'নারী স্বাস্থ্য', englishName: 'Women\'s Health', icon: <Spa /> },
  { name: 'পুরুষ স্বাস্থ্য', englishName: 'Men\'s Health', icon: <Accessibility /> },
  { name: 'শিশুরোগ', englishName: 'Pediatrics', icon: <ChildCare /> },
  { name: 'বৃদ্ধরোগ', englishName: 'Geriatrics', icon: <Elderly /> },
  { name: 'প্রাথমিক চিকিৎসা', englishName: 'First Aid', icon: <LocalHospital /> },
  { name: 'প্রেসক্রিপশন ওষুধ', englishName: 'Prescription Medicines', icon: <MedicalServices /> },
  { name: 'ওটিসি ওষুধ', englishName: 'OTC Medicines', icon: <LocalPharmacy /> },
  { name: 'ক্যান্সার চিকিৎসা', englishName: 'Oncology', icon: <BrightnessLow /> },
  { name: 'স্নায়ুরোগ', englishName: 'Neurology', icon: <Psychology /> },
  { name: 'সংক্রামক রোগ', englishName: 'Infectious Diseases', icon: <BugReport /> },
  { name: 'এন্ডোক্রিনোলজি', englishName: 'Endocrinology', icon: <WaterDrop /> },
  { name: 'ইমিউনোলজি', englishName: 'Immunology', icon: <Shield /> },
  { name: 'রক্তরোগ', englishName: 'Hematology', icon: <Opacity /> },
  { name: 'চক্ষুরোগ', englishName: 'Ophthalmology', icon: <Visibility /> },
  { name: 'অ্যালার্জি ও ইমিউনোলজি', englishName: 'Allergy & Immunology', icon: <Shield /> },
  { name: 'মূত্ররোগ', englishName: 'Urology', icon: <WaterDrop /> },
  { name: 'রিউমাটোলজি', englishName: 'Rheumatology', icon: <Accessibility /> },
  { name: 'অ্যানেস্থেসিওলজি', englishName: 'Anesthesiology', icon: <Healing /> },
  { name: 'প্যালিয়েটিভ কেয়ার', englishName: 'Palliative Care', icon: <Spa /> },
  { name: 'দুর্লভ রোগ', englishName: 'Rare Diseases', icon: <Science /> },
  { name: 'টিকা', englishName: 'Vaccines', icon: <Vaccines /> },
  { name: 'নিউট্রাসিউটিক্যাল', englishName: 'Nutraceuticals', icon: <EmojiFoodBeverage /> },
];

const stores = [
  {
    name: 'মেডিকেয়ার ফার্মেসি',
    englishName: 'MediCare Pharmacy',
    logo: '/images/logos/medicare.png',
    description: 'বিশ্বস্ত ও গুণগত ঔষধের জন্য'
  },
  {
    name: 'হেলথপ্লাস স্টোর',
    englishName: 'HealthPlus Store',
    logo: '/images/logos/healthplus.png',
    description: 'স্বাস্থ্যসেবার সম্পূর্ণ সমাধান'
  },
  {
    name: 'সিটি ফার্মেসি',
    englishName: 'City Pharmacy',
    logo: '/images/logos/city.png',
    description: 'শহরের সবচেয়ে পুরাতন ফার্মেসি'
  },
  {
    name: 'মেডিওয়ার্ল্ড',
    englishName: 'MediWorld',
    logo: '/images/logos/mediworld.png',
    description: 'আধুনিক চিকিৎসা সেবা প্রদানকারী'
  },
  {
    name: 'হেলথকেয়ার প্লাস',
    englishName: 'HealthCare Plus',
    logo: '/images/logos/healthcare.png',
    description: '২৪/৭ ঔষধ সরবরাহ সেবা'
  },
  {
    name: 'পপুলার ফার্মেসি',
    englishName: 'Popular Pharmacy',
    logo: '/images/logos/popular.png',
    description: 'জনপ্রিয় ও সাশ্রয়ী ঔষধের দোকান'
  },
  {
    name: 'লাজ ফার্মা',
    englishName: 'Lazz Pharma',
    logo: '/images/logos/lazz.png',
    description: 'দ্রুত ডেলিভারি ও অনলাইন সেবা'
  },
  {
    name: 'মেডিসিন কর্নার',
    englishName: 'Medicine Corner',
    logo: '/images/logos/corner.png',
    description: 'আপনার এলাকার নির্ভরযোগ্য ফার্মেসি'
  },
];

const medicines = [
  {
    id: 1,
    name: 'নাপা ৫০০মিগ্রা',
    englishName: 'Napa 500mg',
    category: 'ব্যথা নিরাময়',
    store: 'লাজ ফার্মা',
    price: '৳২.০০',
    image: '/images/medicines/m1.png',
    description: 'প্যারাসিটামল একটি ব্যথানাশক ও জ্বরনাশক ঔষধ। হালকা থেকে মাঝারি ব্যথা ও জ্বরের চিকিৎসায় ব্যবহৃত হয়।',
    company: 'বেক্সিমকো ফার্মা'
  },
  {
    id: 2,
    name: 'সেক্লো ৫০০মিগ্রা',
    englishName: 'Seclo 500mg',
    category: 'পরিপাকতন্ত্র',
    store: 'মেডিকেয়ার ফার্মেসি',
    price: '৳১২.০০',
    image: '/images/medicines/m2.png',
    description: 'ওমেপ্রাজল একটি প্রোটন পাম্প ইনহিবিটর যা অম্লজনিত রোগ, গ্যাস্ট্রিক আলসার ও গ্যাস্ট্রোইসোফাগাল রিফ্লাক্স ডিজিজের চিকিৎসায় ব্যবহৃত হয়।',
    company: 'স্কয়ার ফার্মাসিউটিক্যালস'
  },
  {
    id: 3,
    name: 'এসিক্লো ৪০০মিগ্রা',
    englishName: 'Aciclo 400mg',
    category: 'অ্যান্টিবায়োটিক',
    store: 'হেলথপ্লাস স্টোর',
    price: '৳৮.০০',
    image: '/images/medicines/m3.png',
    description: 'অ্যাসাইক্লোভির একটি অ্যান্টিভাইরাল ঔষধ যা হার্পিস সিমপ্লেক্স ভাইরাস সংক্রমণের চিকিৎসায় ব্যবহৃত হয়।',
    company: 'এসকেএফ'
  },
  {
    id: 4,
    name: 'গ্লুকোফেজ ৫০০মিগ্রা',
    englishName: 'Glucophage 500mg',
    category: 'ডায়াবেটিস',
    store: 'সিটি ফার্মেসি',
    price: '৳৪.৫০',
    image: '/images/medicines/m4.png',
    description: 'মেটফরমিন টাইপ ২ ডায়াবেটিসের চিকিৎসায় ব্যবহৃত হয়। এটি রক্তে গ্লুকোজের মাত্রা নিয়ন্ত্রণে সাহায্য করে।',
    company: 'ইনসেপ্টা ফার্মাসিউটিক্যালস'
  },
  {
    id: 5,
    name: 'এমলোডিপিন ৫মিগ্রা',
    englishName: 'Amlodipine 5mg',
    category: 'হৃদরোগ সম্পর্কিত',
    store: 'মেডিওয়ার্ল্ড',
    price: '৳৩.০০',
    image: '/images/medicines/m5.png',
    description: 'এমলোডিপিন উচ্চ রক্তচাপ ও এনজাইনার চিকিৎসায় ব্যবহৃত একটি ক্যালসিয়াম চ্যানেল ব্লকার।',
    company: 'রেনাটা লিমিটেড'
  },
  {
    id: 6,
    name: 'সালবুটামল ইনহেলার',
    englishName: 'Salbutamol Inhaler',
    category: 'শ্বাসযন্ত্র',
    store: 'হেলথকেয়ার প্লাস',
    price: '৳১২০.০০',
    image: '/images/medicines/m6.png',
    description: 'সালবুটামল ইনহেলার হাঁপানি ও ব্রঙ্কোস্পাজমের তাৎক্ষণিক উপশমের জন্য ব্যবহৃত হয়।',
    company: 'গ্ল্যাক্সো স্মিথক্লাইন'
  },
  {
    id: 7,
    name: 'লোরাটাডিন ১০মিগ্রা',
    englishName: 'Loratadine 10mg',
    category: 'অ্যালার্জি ও ইমিউনোলজি',
    store: 'পপুলার ফার্মেসি',
    price: '৳২.৫০',
    image: '/images/medicines/m7.png',
    description: 'লোরাটাডিন একটি অ্যান্টিহিস্টামিন যা অ্যালার্জিক রাইনাইটিস ও আর্টিকারিয়ার চিকিৎসায় ব্যবহৃত হয়।',
    company: 'এসিআই লিমিটেড'
  },
  {
    id: 8,
    name: 'ভিটামিন ডি৩ ৮০০ আইইউ',
    englishName: 'Vitamin D3 800 IU',
    category: 'ভিটামিন ও সম্পূরক',
    store: 'মেডিসিন কর্নার',
    price: '৳৬.০০',
    image: '/images/medicines/m8.png',
    description: 'ভিটামিন ডিৃ হাড়ের স্বাস্থ্য ও ক্যালসিয়াম শোষণের জন্য প্রয়োজনীয়। ভিটামিন ডি এর অভাবজনিত সমস্যায় ব্যবহৃত হয়।',
    company: 'অপসোনিন ফার্মা'
  },
  {
    id: 9,
    name: 'আইবুপ্রোফেন ৪০০মিগ্রা',
    englishName: 'Ibuprofen 400mg',
    category: 'ব্যথা নিরাময়',
    store: 'লাজ ফার্মা',
    price: '৳৩.৫০',
    image: '/images/medicines/m9.png',
    description: 'আইবুপ্রোফেন একটি নন-স্টেরয়েডাল অ্যান্টি-ইনফ্ল্যামেটরি ড্রাগ যা ব্যথা, প্রদাহ ও জ্বরের চিকিৎসায় ব্যবহৃত হয়।',
    company: 'হেক্সাফার্মা'
  },
  {
    id: 10,
    name: 'সিপ্রোফ্লক্সাসিন ৫০০মিগ্রা',
    englishName: 'Ciprofloxacin 500mg',
    category: 'অ্যান্টিবায়োটিক',
    store: 'মেডিকেয়ার ফার্মেসি',
    price: '৳৮.০০',
    image: '/images/medicines/m10.png',
    description: 'সিপ্রোফ্লক্সাসিন একটি ব্রড-স্পেকট্রাম অ্যান্টিবায়োটিক যা বিভিন্ন ব্যাকটেরিয়াল সংক্রমণের চিকিৎসায় ব্যবহৃত হয়।',
    company: 'স্কয়ার ফার্মাসিউটিক্যালস'
  },
  {
    id: 11,
    name: 'ডমপেরিডন ১০মিগ্রা',
    englishName: 'Domperidone 10mg',
    category: 'পরিপাকতন্ত্র',
    store: 'হেলথপ্লাস স্টোর',
    price: '৳২.০০',
    image: '/images/medicines/m11.png',
    description: 'ডমপেরিডন বমি বমি ভাব, বমি ও পেটের গ্যাসের সমস্যার চিকিৎসায় ব্যবহৃত হয়।',
    company: 'জেনেরিক ফার্মা'
  },
  {
    id: 12,
    name: 'ক্লোনাজেপাম ০.৫মিগ্রা',
    englishName: 'Clonazepam 0.5mg',
    category: 'মানসিক স্বাস্থ্য',
    store: 'সিটি ফার্মেসি',
    price: '৳৪.০০',
    image: '/images/medicines/m12.png',
    description: 'ক্লোনাজেপাম খিঁচুনি, প্যানিক ডিসঅর্ডার ও উদ্বেগজনিত রোগের চিকিৎসায় ব্যবহৃত হয়।',
    company: 'ড্রাগ ইন্টারন্যাশনাল'
  },
  {
    id: 13,
    name: 'আয়রন ফলিক এসিড',
    englishName: 'Iron Folic Acid',
    category: 'নারী স্বাস্থ্য',
    store: 'মেডিওয়ার্ল্ড',
    price: '৳১.৫০',
    image: '/images/medicines/m13.png',
    description: 'আয়রন ফলিক এসিড রক্তশূন্যতা প্রতিরোধ ও চিকিৎসায় ব্যবহৃত হয়। গর্ভবতী মহিলাদের জন্য বিশেষভাবে প্রয়োজনীয়।',
    company: 'বেক্সিমকো ফার্মা'
  },
  {
    id: 14,
    name: 'সেফিক্সিম ২০০মিগ্রা',
    englishName: 'Cefixime 200mg',
    category: 'অ্যান্টিবায়োটিক',
    store: 'হেলথকেয়ার প্লাস',
    price: '৳১৫.০০',
    image: '/images/medicines/m14.png',
    description: 'সেফিক্সিম একটি তৃতীয় প্রজন্মের সেফালোস্পোরিন অ্যান্টিবায়োটিক যা বিভিন্ন সংক্রমণের চিকিৎসায় ব্যবহৃত হয়।',
    company: 'এসকেএফ'
  },
  {
    id: 15,
    name: 'ক্যালসিয়াম কার্বনেট ৫০০মিগ্রা',
    englishName: 'Calcium Carbonate 500mg',
    category: 'ভিটামিন ও সম্পূরক',
    store: 'পপুলার ফার্মেসি',
    price: '৳২.০০',
    image: '/images/medicines/m15.png',
    description: 'ক্যালসিয়াম কার্বনেট হাড় ও দাঁতের স্বাস্থ্যের জন্য প্রয়োজনীয়। ক্যালসিয়ামের অভাবজনিত সমস্যায় ব্যবহৃত হয়।',
    company: 'রেনাটা লিমিটেড'
  }
];

const MedicineBazar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { addToCart } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreTab, setSelectedStoreTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [typingText, setTypingText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  
  // Prescription flow states
  const [showPrescriptionFlow, setShowPrescriptionFlow] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPharmacy, setSelectedPharmacy] = useState('');
  const [courseDuration, setCourseDuration] = useState('');
  const [customDays, setCustomDays] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [mobilePopupTab, setMobilePopupTab] = useState('search');
  const [searchTerm, setSearchTerm] = useState('');

  const currentLanguage = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = currentLanguage === 'en' ? medicineBazarEn : currentLanguage === 'np' ? medicineBazarNp : currentLanguage === 'pk' ? medicineBazarPk : currentLanguage === 'bt' ? medicineBazarBt : currentLanguage === 'de' ? medicineBazarDe : currentLanguage === 'sl' ? medicineBazarSl : currentLanguage === 'af' ? medicineBazarAf : currentLanguage === 'cn' ? medicineBazarCn : medicineBazarBn;
  const searchPlaceholders = t.search.placeholders;

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  // Typewriter effect for search placeholders
  useEffect(() => {
    const currentPlaceholder = searchPlaceholders[placeholderIndex];
    
    const handleTyping = () => {
      if (isDeleting) {
        setTypingText(prev => prev.slice(0, -1));
        setTypingSpeed(50);
        
        if (typingText === '') {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % searchPlaceholders.length);
          setTypingSpeed(150);
        }
      } else {
        setTypingText(currentPlaceholder.slice(0, typingText.length + 1));
        setTypingSpeed(150);
        
        if (typingText === currentPlaceholder) {
          setTypingSpeed(2000);
          setIsDeleting(true);
        }
      }
    };
    
    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [typingText, isDeleting, placeholderIndex, searchPlaceholders, typingSpeed]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store);
    setSelectedStoreTab(0); // Reset tab to "All" when selecting a new store
  };

  const handlePrescriptionUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPrescriptionFile(file);
      // Show the prescription flow dialog after successful upload
      setShowPrescriptionFlow(true);
      setActiveStep(0);
    }
  };

  const handlePrescriptionFlowNext = () => {
    if (activeStep === 0 && selectedPharmacy) {
      setActiveStep(1);
    } else if (activeStep === 1 && courseDuration) {
      setActiveStep(2);
    }
  };

  const handlePrescriptionFlowBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmitPrescriptionOrder = () => {
    setShowPrescriptionFlow(false);
    setShowSuccessMessage(true);
    // Reset form
    setActiveStep(0);
    setSelectedPharmacy('');
    setCourseDuration('');
    setCustomDays('');
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
  };

  const handleMobilePopupOpen = () => {
    setShowMobilePopup(true);
  };

  const handleMobilePopupClose = () => {
    setShowMobilePopup(false);
    // Clear search term when closing popup
    setSearchTerm('');
  };

  const handleSearch = () => {
    setSearchQuery(searchTerm);
    // Close mobile popup after search
    setShowMobilePopup(false);
  };

  const handleAddToCart = (product) => {
    
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      store: product.store,
      stock: product.stock || 10
    };
    
    addToCart(cartProduct, 1);
  };

  // Get unique categories for the selected store
  const getStoreCategoriesWithCounts = () => {
    if (!selectedStore) return [];
    
    const storeProducts = medicines.filter(medicine => medicine.store === selectedStore.name);
    const categoryMap = new Map();
    
    // Count medicines in each category
    storeProducts.forEach(medicine => {
      const count = categoryMap.get(medicine.category) || 0;
      categoryMap.set(medicine.category, count + 1);
    });
    
    // Convert to array and add "All" option
    const categories = [
      { name: 'সকল ঔষধ', count: storeProducts.length },
      ...Array.from(categoryMap.entries()).map(([name, count]) => ({ name, count }))
    ];
    
    return categories;
  };

  const handleStoreTabChange = (event, newValue) => {
    setSelectedStoreTab(newValue);
  };

  const filteredMedicines = medicines.filter((medicine) => {
    const matchesCategory = !selectedCategory || medicine.category === selectedCategory;
    const matchesStore = !selectedStore || medicine.store === selectedStore.name;
    
    // Add store tab filtering
    let matchesStoreTab = true;
    if (selectedStore && selectedStoreTab > 0) {
      const storeCategories = getStoreCategoriesWithCounts();
      const selectedTabCategory = storeCategories[selectedStoreTab]?.name;
      matchesStoreTab = medicine.category === selectedTabCategory;
    }
    
    const matchesSearch = !searchQuery || 
      medicine.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesStoreTab && matchesSearch;
  });

  return (
    <>
      <Header />
      <Box sx={{ flexGrow: 1, p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/pharmacy.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />
          
          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={typingText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: { xs: 0, sm: 2 },
              mt: { xs: 1, sm: 0 },
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              flexGrow: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
              '& input::placeholder': {
                animation: 'blink 1s infinite',
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                </InputAdornment>
              ),
            }}
          />
        </SearchBarHeader>

        {/* Mobile Filter Button */}
        {isMobile && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleMobilePopupOpen}
            sx={{
              mb: 2,
              py: 1.5,
              background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
              color: 'white',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: 3,
              '&:hover': {
                background: 'linear-gradient(45deg, #4a2496 30%, #1565c0 90%)',
                boxShadow: 6,
              },
            }}
            startIcon={<Search />}
          >
            {t.sidebar.categoriesTitle} & {t.sidebar.storesTitle}
          </Button>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 },
            display: { xs: 'none', md: 'block' }
          }}>
            <Sidebar elevation={3}>
              {/* Categories */}
              <SectionHeader>
                <LocalHospital sx={{ mr: 1 }} />
                <Typography variant="h6">{t.sidebar.categoriesTitle}</Typography>
              </SectionHeader>
              <CategoryList>
                {categories.map((category, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleCategoryClick(category.name)}
                    selected={selectedCategory === category.name}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {category.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={category.name}
                      secondary={category.englishName}
                    />
                  </ListItem>
                ))}
              </CategoryList>

              {/* Stores */}
              <SectionHeader sx={{ mt: 3 }}>
                <Store sx={{ mr: 1 }} />
                <Typography variant="h6">{t.sidebar.storesTitle}</Typography>
              </SectionHeader>
              <StoreList>
                {stores.map((store, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleStoreClick(store)}
                    selected={selectedStore?.name === store.name}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <DefaultPharmacyLogo>
                        <LocalPharmacy fontSize="small" color="primary" />
                      </DefaultPharmacyLogo>
                    </ListItemIcon>
                    <ListItemText 
                      primary={store.name}
                      secondary={`${store.englishName} • ${store.description}`}
                    />
                  </ListItem>
                ))}
              </StoreList>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Prescription Upload */}
              <PrescriptionUpload>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: '250px' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Description sx={{ mr: 1 }} />
                      {t.upload.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.upload.subtitle}
                    </Typography>
                    {prescriptionFile && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'success.main', fontWeight: 'medium' }}>
                        {t.upload.uploadedPrefix} {prescriptionFile.name}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handlePrescriptionUpload}
                      style={{ display: 'none' }}
                      id="prescription-upload"
                    />
                    <label htmlFor="prescription-upload">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<Description />}
                        sx={{
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                          color: 'white',
                          px: 3,
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '1rem',
                          fontWeight: 'medium',
                          boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                            boxShadow: '0 4px 8px 3px rgba(33, 203, 243, .4)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {prescriptionFile ? t.upload.replaceFile : t.upload.chooseFile}
                      </Button>
                    </label>
                  </Box>
                </Box>
              </PrescriptionUpload>
              
              {/* Store Details Section */}
              {selectedStore && (
                <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(45deg, #f8f9fa 30%, #e3f2fd 90%)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #bbdefb',
                      mr: 3
                    }}>
                      <LocalPharmacy sx={{ fontSize: 30, color: '#1976d2' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        {selectedStore.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                        {selectedStore.englishName}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedStore.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOn sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            {t.store.serviceAreaLabel}: {t.store.nationwide}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocalPharmacy sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            {t.store.medicineCountLabel}: {filteredMedicines.length}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setSelectedStore(null)}
                      startIcon={<Close />}
                      sx={{ ml: 2 }}
                    >
                      {t.store.close}
                    </Button>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#1976d2', mb: 1 }}>
                    {selectedStore.name} {t.store.productsOfSuffix}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t.store.descriptionPrefix} {selectedStore.name} {t.store.descriptionSuffix}
                  </Typography>
                  
                  {/* Category Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs 
                      value={selectedStoreTab} 
                      onChange={handleStoreTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        '& .MuiTab-root': {
                          minWidth: 'auto',
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#1976d2',
                        }
                      }}
                    >
                      {getStoreCategoriesWithCounts().map((category, index) => (
                        <Tab 
                          key={index}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                                {category.name}
                              </Typography>
                              <Box sx={{ 
                                backgroundColor: selectedStoreTab === index ? '#1976d2' : '#e0e0e0',
                                color: selectedStoreTab === index ? 'white' : '#666',
                                borderRadius: '12px',
                                px: 1,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                minWidth: '20px',
                                textAlign: 'center'
                              }}>
                                {category.count}
                              </Box>
                            </Box>
                          }
                          sx={{
                            color: selectedStoreTab === index ? '#1976d2' : 'text.secondary',
                            '&.Mui-selected': {
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                </Paper>
              )}
              
              {/* Medicine Grid */}
              {filteredMedicines.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredMedicines.map((medicine) => (
                    <Box key={medicine.id} sx={{ display: 'flex', width: '100%' }}>
                      <MedicineCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={medicine.image}
                            alt={medicine.name}
                            onError={(e) => {
                              e.target.src = '/images/medicines/default.png';
                            }}
                          />
                          <StyledBadge>
                            {medicine.category}
                          </StyledBadge>
                        </Box>
                        <CardContent sx={{ 
                          flexGrow: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important' // Override default padding bottom
                        }}>
                          <Box>
                            <Typography gutterBottom variant="h6" component="div">
                              {medicine.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {medicine.englishName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {medicine.company}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {medicine.description}
                            </Typography>
                          </Box>
                          
                          <Box sx={{
                            mt: 'auto', // Push this box to the bottom
                            pt: 2 // Add some spacing from the content above
                          }}>
                            <Typography variant="h6" color="primary" sx={{ mb: 1, textAlign: 'center' }}>
                              {medicine.price}
                            </Typography>
                            <Button 
                              variant="contained" 
                              size="small"
                              fullWidth
                              onClick={() => handleAddToCart(medicine)}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              {t.card.addToCart}
                            </Button>
                          </Box>
                        </CardContent>
                      </MedicineCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    {t.empty.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t.empty.subtitle}
                  </Typography>
                </Box>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>

      {/* Prescription Flow Dialog */}
      <Dialog 
        open={showPrescriptionFlow} 
        onClose={() => setShowPrescriptionFlow(false)}
        maxWidth="md"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
          <CheckCircle />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t.prescriptionFlow.successTitle}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {t.prescriptionFlow.instruction}
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} orientation="vertical">
            {/* Step 1: Select Pharmacy */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t.prescriptionFlow.step1.title}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t.prescriptionFlow.step1.subtitle}
                </Typography>
                
                <RadioGroup
                  value={selectedPharmacy}
                  onChange={(e) => setSelectedPharmacy(e.target.value)}
                >
                  {stores.map((store, index) => (
                    <FormControlLabel
                      key={index}
                      value={store.name}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            backgroundColor: '#e3f2fd',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #bbdefb'
                          }}>
                            <LocalPharmacy sx={{ fontSize: 20, color: '#1976d2' }} />
                          </Box>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {store.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {store.description}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        mb: 1,
                        p: 1,
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    />
                  ))}
                </RadioGroup>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handlePrescriptionFlowNext}
                    disabled={!selectedPharmacy}
                    endIcon={<ArrowForward />}
                  >
                    {t.prescriptionFlow.step1.next}
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Step 2: Select Course Duration */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t.prescriptionFlow.step2.title}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t.prescriptionFlow.step2.subtitle}
                </Typography>
                
                <RadioGroup
                  value={courseDuration}
                  onChange={(e) => setCourseDuration(e.target.value)}
                >
                  <FormControlLabel
                    value="full"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assignment sx={{ color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t.prescriptionFlow.step2.fullCourse}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {currentLanguage === 'en' ? 'Full duration as per doctor’s advice' : 'ডাক্তারের নির্দেশ অনুযায়ী পূর্ণ মেয়াদের ঔষধ'}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      mb: 1,
                      p: 1,
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  />
                  
                  <FormControlLabel
                    value="5"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ color: 'warning.main' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t.prescriptionFlow.step2.fiveDays}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      mb: 1,
                      p: 1,
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  />
                  
                  <FormControlLabel
                    value="10"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ color: 'info.main' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t.prescriptionFlow.step2.tenDays}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      mb: 1,
                      p: 1,
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  />
                  
                  <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ color: 'success.main' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t.prescriptionFlow.step2.customDays}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      mb: 1,
                      p: 1,
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  />
                </RadioGroup>

                {courseDuration === 'custom' && (
                  <TextField
                    fullWidth
                    label={t.prescriptionFlow.step2.customDaysLabel}
                    type="number"
                    value={customDays}
                    onChange={(e) => setCustomDays(e.target.value)}
                    sx={{ mt: 2 }}
                    placeholder={t.prescriptionFlow.step2.customDaysPlaceholder}
                    inputProps={{ min: 1, max: 365 }}
                  />
                )}

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    onClick={handlePrescriptionFlowBack}
                    variant="outlined"
                  >
                    {t.prescriptionFlow.step2.back}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handlePrescriptionFlowNext}
                    disabled={!courseDuration || (courseDuration === 'custom' && !customDays)}
                    endIcon={<ArrowForward />}
                  >
                    {t.prescriptionFlow.step2.next}
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* Step 3: Confirmation */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t.prescriptionFlow.step3.title}
                </Typography>
              </StepLabel>
              <StepContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t.prescriptionFlow.step3.summaryTitle}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2">
                      • <strong>{t.prescriptionFlow.step3.pharmacyLabel}:</strong> {selectedPharmacy}
                    </Typography>
                    <Typography variant="body2">
                      • <strong>{t.prescriptionFlow.step3.durationLabel}:</strong> {
                        courseDuration === 'full' ? t.prescriptionFlow.step2.fullCourse :
                        courseDuration === 'custom' ? `${customDays} ${currentLanguage === 'en' ? 'days' : 'দিন'}` :
                        `${courseDuration} ${currentLanguage === 'en' ? 'days' : 'দিন'}`
                      }
                    </Typography>
                    <Typography variant="body2">
                      • <strong>{t.prescriptionFlow.step3.prescriptionLabel}:</strong> {prescriptionFile?.name}
                    </Typography>
                  </Box>
                </Alert>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    onClick={handlePrescriptionFlowBack}
                    variant="outlined"
                  >
                    {t.prescriptionFlow.step3.back}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitPrescriptionOrder}
                    color="success"
                    endIcon={<CheckCircle />}
                  >
                    {t.prescriptionFlow.step3.confirm}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccessMessage} 
          severity="success" 
          sx={{ 
            width: '100%',
            fontSize: '1rem',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t.prescriptionFlow.snackbar.successTitle}
            </Typography>
            <Typography variant="body2">
              <ContactPhone sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              {t.prescriptionFlow.snackbar.message}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Mobile Popup for Search, Categories, and Stores */}
      <Dialog
        open={showMobilePopup}
        onClose={handleMobilePopupClose}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            margin: 0,
            borderRadius: 0,
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h6">
            {t.sidebar.categoriesTitle} & {t.sidebar.storesTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleMobilePopupClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Tabs 
            value={mobilePopupTab} 
            onChange={(e, newValue) => setMobilePopupTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
                            <Tab label="Search" value="search" />
            <Tab label={t.sidebar.categoriesTitle} value="categories" />
            <Tab label={t.sidebar.storesTitle} value="stores" />
          </Tabs>

          {/* Search Tab */}
          {mobilePopupTab === 'search' && (
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search medicines..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearch}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleSearch}
              >
                Search
              </Button>
            </Box>
          )}

          {/* Categories Tab */}
          {mobilePopupTab === 'categories' && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalHospital sx={{ mr: 1 }} />
                {t.sidebar.categoriesTitle}
              </Typography>
              <List>
                {categories.map((category, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => {
                      handleCategoryClick(category.name);
                      handleMobilePopupClose();
                    }}
                    selected={selectedCategory === category.name}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {category.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={category.name}
                      secondary={category.englishName}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Stores Tab */}
          {mobilePopupTab === 'stores' && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Store sx={{ mr: 1 }} />
                {t.sidebar.storesTitle}
              </Typography>
              <List>
                {stores.map((store, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => {
                      handleStoreClick(store);
                      handleMobilePopupClose();
                    }}
                    selected={selectedStore?.name === store.name}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <DefaultPharmacyLogo>
                        <LocalPharmacy fontSize="small" color="primary" />
                      </DefaultPharmacyLogo>
                    </ListItemIcon>
                    <ListItemText 
                      primary={store.name}
                      secondary={`${store.englishName} • ${store.description}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </>
  );
};

export default MedicineBazar;